import { Button, Flex } from '@mantine/core'
import { useEffect, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useQueryGetAllCustomers } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreCustomer, type Maybe } from '@flecto/types'
import { Page } from '@flecto/ui/saas'

import { useDrawerCustomer } from '../components/DrawerCustomer/useDrawerCustomer'
import { FiltersCustomers } from '../components/FiltersCustomers/FiltersCustomers'
import { TableCustomers } from '../components/TableCustomers/TableCustomers'
import { usePagination } from '../components/TableCustomers/usePagination'

export function PageCustomersList() {
  const [selectedCustomer, setSelectedCustomer] =
    useState<Maybe<Partial<CoreCustomer>>>(undefined)
  const { openDrawer } = useDrawerCustomer({
    customer: selectedCustomer,
    setCustomer: setSelectedCustomer,
  })

  const { t } = useTranslation('saas', { keyPrefix: 'customers' })
  const track = useAnalytics('segment')

  const {
    currentPage,
    totalRecords,
    searchTerm,
    filteredCustomers,
    pageSize,
    handlePageChange,
    setTotalRecords,
    handleSearch,
    setFilteredCustomers,
  } = usePagination(10)

  const {
    data: customers,
    isLoading,
    isFetched,
  } = useQueryGetAllCustomers({
    q: searchTerm,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    order: 'desc',
  })

  const handleRowClick = ({ record }: { record: CoreCustomer }) =>
    setSelectedCustomer(record)

  const handleNewCustomer = () => {
    track('CUSTOMER__NEW', {})
    setSelectedCustomer(undefined)
    openDrawer()
  }

  useEffect(() => {
    if (selectedCustomer) {
      openDrawer()
    }
  }, [selectedCustomer, openDrawer])

  useEffect(() => {
    if (isFetched) {
      setTotalRecords(customers?.meta?.total_count ?? 0)
      setFilteredCustomers(customers?.data ?? [])
    }
  }, [customers, isFetched, setTotalRecords, setFilteredCustomers])

  return (
    <Page>
      <Page.Title>{t('page_title')}</Page.Title>
      <Page.Actions>
        <Button variant='filled' onClick={handleNewCustomer}>
          {t('button_label_add_customer')}
        </Button>
      </Page.Actions>
      <Page.Content>
        <Flex direction='column' gap='md'>
          <Flex justify='flex-end'>
            <FiltersCustomers onFilter={handleSearch} />
          </Flex>

          <TableCustomers
            customers={filteredCustomers}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            withPaginationControlled={{
              currentPage,
              totalRecords,
              onPageChange: handlePageChange,
            }}
          />
        </Flex>
      </Page.Content>
    </Page>
  )
}

export default PageCustomersList
